import React, {useEffect, useState} from "react";
import {isUndefined} from "lodash";
import Graph from "../components/Graph";
import axios from "axios";

const QuarterlyFinancialResults = () => {
  const [rawData, setRawData] = useState({});
  const [groupedData, setGroupedData] = useState({});

  const field2TranslationMapping = [
    {
      name: "field_quarterly_sales",
      classes: "color-text-orange",
      tr: Drupal.t("Sales")
    },
    {
      name: "field_quarterly_sales_growth",
      tr: Drupal.t("Sales growth"),
      sup: 2 // supscript html element
    },
    {
      name: "field_quarterly_ebitda",
      classes: "color-text-orange",
      tr: Drupal.t("EBITDA")
    },
    {
      name: "amrest_field_quarterly_ebitda_per",
      tr: Drupal.t("EBITDA (%)"),
      type: "perc"
    },
    {
      name: "field_quarterly_ebitda_adj",
      classes: "color-text-orange",
      tr: Drupal.t("Adjusted EBITDA"),
      sup: 3
    },
    {
      name: "amrest_field_quarterly_ebitda_adj_per",
      tr: Drupal.t("Adjusted EBITDA") + " (%)",
      type: "perc"
    },
    {
      name: "field_quarterly_ebit",
      classes: "color-text-orange",
      tr: Drupal.t("EBIT")
    },
    {
      name: "amrest_field_quarterly_ebit_per",
      tr: Drupal.t("EBIT (%)"),
      type: "perc"
    },
    {
      name: "field_quarterly_profit",
      classes: "color-text-orange",
      tr: Drupal.t("Profit for the period"),
      sup: 4
    },
    {
      name: "amrest_field_quarterly_profit_per",
      tr: Drupal.t("Profit for the period") + " (%)",
      type: "perc"
    },
    {
      name: "field_quarterly_debt_net",
      classes: "color-text-orange",
      tr: Drupal.t("Net debt")
    },
    {
      name: "field_quarterly_net_debt_ebitda",
      tr: Drupal.t("Net debt") + "/" + Drupal.t("EBITDA") + "*"
    },
    {
      name: "field_quarterly_capex",
      classes: "color-text-orange",
      tr: Drupal.t("Capital investment")
    }
  ];

  const frsTranslations = {
    default: Drupal.t("Non IFRS16"),
    ifrs16: Drupal.t("IFRS16")
  }

  useEffect(() => {
    getData();
  }, []);

  function getData() {
    const query = "/api/node/quarterly_financial_result";

    axios
      .get(query)
      .catch(error => {
        console.error(error);
      })
      .then(resp => {
        if (resp && resp.status === 200) {
          setRawData(resp.data);
          groupData(resp.data);
        } else {
          console.error("No data");
        }
      });
  }

  // data grouped by years (as index)
  function groupData(rawData) {
    let data = [];
    let obj;
    let year;
    let quarter;
    let frs;
    for (let i = 0; i < rawData.length; i++) {
      obj = rawData[i];
      year = obj.field_year[0].value;
      if (isUndefined(data[year])) {
        data[year] = {};
      }
      quarter = obj.field_quarter[0].value;
      if (isUndefined(data[year][quarter])) {
        data[year][quarter] = {};
      }

      frs = obj.field_frs[0].value;
      data[year][quarter][frs] = obj;

      // data[year][quarter] = obj;
    }

    setGroupedData(data);
  }

  function createTableHeaders() {
    let headers = [];
    let headersRow = [];
    let yearObj;
    let quarterKeys;

    headers.push(
      <th className="text-nowrap pl-4 border-right border-top-0 border-bottom-0" key={"header-currency"} rowSpan="2">
        {Drupal.t("[mln EUR]")}
      </th>
    );

    // add TTM column (first row)
    headers.push(<th className="border-top-0 border-bottom-0 text-center border-right" key="httm1">
      TTM <sup className="ml-1">[1]</sup>
    </th>)

    // first header row, with Quarters
    for (let year = groupedData.length - 1; year >= 0; year--) {
      yearObj = groupedData[year];
      if (!isUndefined(yearObj)) {
        for (let quarter in yearObj) {
          headers.push(createHeaderCell(yearObj, year, quarter));
        }
      }
    }

    headersRow.push(<tr className="row-quarters border-bottom-0 text-nowrap" key={"hr1"}>{headers}</tr>);

    headers = [];

    // add TTM column (second row)
    headers.push(<th className="border-top-0 border-bottom-0 text-center border-right" key="httm2">
      {Drupal.t("Non IFRS16")}
    </th>)

    // second header row, with ifrs (other cells have row- or colspan)
    for (let year = groupedData.length - 1; year >= 0; year--) {
      yearObj = groupedData[year];
      if (!isUndefined(yearObj)) {
        for (let quarter in yearObj) {
          quarterKeys = Object.keys(yearObj[quarter]);
          quarterKeys.sort(sortByOrder);
          if (quarterKeys.length > 1) {
            for (let qk = 0; qk < quarterKeys.length; qk++) {
              headers.push(<th className={"border-top-0 border-bottom-0 " + (qk === 0 ? "border-left" : "")}
                               key={"hr2c" + year + quarter + qk}>
                {frsTranslations[quarterKeys[qk]]}
              </th>);
            }
          }
        }
      }
    }

    headersRow.push(<tr className="row-frs text-nowrap" key="hr2">{headers}</tr>);

    return headersRow;
  }

  function createHeaderCell(yearObj, year, quarter) {
    let quarterKeys = Object.keys(yearObj[quarter]);
    return <th
      className="text-nowrap border-right border-top-0 border-bottom-0 text-center"
      key={"hr1c" + year + quarter}
      rowSpan={quarterKeys.length > 1 ? 1 : 2}
      colSpan={quarterKeys.length > 1 ? quarterKeys.length : 1}
    >
      {`Q${yearObj[quarter][quarterKeys[0]].field_quarter[0].value} ${year}`}
    </th>
  }

  function generateTableBody() {
    let cells;
    let rows = [];
    let yearObj;
    let field;
    let quarterKeys;
    let salesTtm = sumLastPeriods('field_quarterly_sales');

    for (let f = 0; f < field2TranslationMapping.length; f++) {
      field = field2TranslationMapping[f];
      cells = [];

      cells.push(
        <td
          className={
            "font-weight-bold text-nowrap border-right border-top-0 pl-4 pr-4 " +
            field.classes ?? ""
          }
          key={"translation" + f}
        >
          {field.tr}
          {field.sup ? <sup className="ml-1">[{field.sup}]</sup> : null}
        </td>
      );

      // add TTM cell
      cells.push(<td
        className={
        "text-nowrap text-right border-top-0 border-right " +
        (f % 2 === 0 ? "font-weight-bold" : "")
      }
      key={"ttm" + f}>{displayValue(field, calculateTtmPerField(field, salesTtm))}</td>);

      for (let year = groupedData.length - 1; year >= 0; year--) {
        yearObj = groupedData[year];
        if (!isUndefined(yearObj)) {
          for (let quarter in yearObj) {
            quarterKeys = Object.keys(yearObj[quarter]);
            quarterKeys.sort(sortByOrder);
            for (let qk = 0; qk < quarterKeys.length; qk++) {
              cells.push(
                <td
                  className={
                    "text-nowrap text-right border-top-0 border-right " +
                    (f % 2 === 0 ? "font-weight-bold" : "")}
                  key={"" + f + year + quarter + qk}>
                  {displayValue(field, yearObj[quarter][quarterKeys[qk]][field.name][0].value)}
                </td>
              );
            }
          }
        }
      }

      rows.push(<tr className="text-nowrap" key={"row" + f}>{cells}</tr>);
    }

    return rows;
  }

  function sortByOrder(a, b, customOrder) {
    if (isUndefined(customOrder)) {
      customOrder = ["default", "ifrs16"];
    }

    return customOrder.indexOf(a) - customOrder.indexOf(b);
  }

  function displayValue(field, value) {
    let preparedValue = parseFloat(value);

    if (preparedValue === 0) {
      return "-";
    }

    if (field.name !== 'field_quarterly_net_debt_ebitda') {
      preparedValue = Math.round((preparedValue + Number.EPSILON) * 10) / 10;
      preparedValue = preparedValue.toFixed(1);
    } else {
      preparedValue = preparedValue.toFixed(2);
    }

    preparedValue = decimalSeparatorByLang(preparedValue);

    if (field.type === 'perc') {
      preparedValue = preparedValue + "%"
    }

    return preparedValue;
  }

  function decimalSeparatorByLang(value) {
    let valueString = value.toString();
    let lang = document.documentElement.getAttribute('lang');

    switch (lang) {
      case 'es':
      case 'pl':
        return valueString.replace('.', ',');
      default:
        return valueString;
    }
  }

  function sumLastPeriods(fieldName, lastPeriods) {
    const LAST_PERIODS = lastPeriods || 4;
    let sum = 0;
    let yearObj;
    let periodsCounter = 1;
    let value;
    let quarterKeys;

    for (let year = groupedData.length - 1; year >= 0; year--) {
      yearObj = groupedData[year];
      if (!isUndefined(yearObj)) {
        quarterKeys = Object.keys(yearObj).sort((a, b) => {return b - a}); // we need reverse order
        for (let quarter in yearObj) {
          value = parseFloat(yearObj[quarter]["default"][fieldName][0].value);
          sum += value;
          if(periodsCounter === LAST_PERIODS) {
              return sum;
          }
          periodsCounter++;
        }
      }
    }
  }

  function calculateTtmPerField(field, salesTtm) {
    switch (field.name) {
      case "field_quarterly_sales":
        return salesTtm;
      case "field_quarterly_sales_growth":
      case "field_quarterly_ebitda":
      case "field_quarterly_ebitda_adj":
      case "field_quarterly_ebit":
      case "field_quarterly_profit":
      case "field_quarterly_debt_net":
      case "field_quarterly_net_debt_ebitda":
      case "field_quarterly_capex":
        return sumLastPeriods(field.name);
      case "amrest_field_quarterly_ebitda_per":
        return (sumLastPeriods("field_quarterly_ebitda") / salesTtm) * 100;
      case "amrest_field_quarterly_ebitda_adj_per":
        return (sumLastPeriods("field_quarterly_ebitda_adj") / salesTtm) * 100;
      case "amrest_field_quarterly_ebit_per":
        return (sumLastPeriods("field_quarterly_ebit") / salesTtm) * 100;
      case "amrest_field_quarterly_profit_per":
        return (sumLastPeriods("field_quarterly_profit") / salesTtm) * 100;
    }
  }

  function createLegend() {
    return <div className="legend bg-white font-size-dot85rem pl-4 mb-5">
          <p className="mb-1">[1] {Drupal.t("Trailing 12 months")}</p>
          <p className="mb-1">[2] {Drupal.t("The growth vs corresponding period in the previous year")}</p>
          <p className="mb-1">[3] {Drupal.t("EBITDA adjusted by costs of new openings (Start-up), costs of mergers and acquisitions (all material costs related to professional services, connected with finalized merger or acquisition and directly related to the transaction), corrections in indirect taxes and the effect of SOP exercise method modification (difference in accounting cost of employee benefits accounted under cash settled versus equity settled option plan)")}</p>
          <p className="mb-1">[4] {Drupal.t("Attributable to AmRest shareholders")}</p>
          <p className="mb-1">*{Drupal.t("EBITDA measured accordingly to the loan agreements")}</p>
        </div>
  }

  return (
    <>
      <table className="table table-striped table-responsive font-size-dot90rem table-first-column-sticky">
        <thead>{createTableHeaders()}</thead>
        <tbody>{generateTableBody()}</tbody>
      </table>
      {createLegend()}
      <Graph groupedData={groupedData}/>
      <p className="ml-96p">
        <em>{Drupal.t("The data before 2019 were not calculated with the impact of the IFRS 16")}</em></p>
    </>
  );
};

export default QuarterlyFinancialResults;
